import { useMemo } from 'react';
import { useStoredState } from 'hooks/useStoredState';
import { FIT_DEFAULTS, StoredFit, clampFitWindow, reviveFit } from './auxFitStorage';
import {
  LinearFit,
  ScatterSplit,
  fitLinePoints,
  linearFit,
  linearFitFixedIntercept,
  PlotBox,
  partitionByRange,
  zoomBox,
} from './auxSensorChannels';

/** One series' split plus the fit over its included points. */
export interface ScatterFitResult extends ScatterSplit {
  fit: LinearFit | null;
  /** Two-point line spanning the fitted points, or null when there is no fit. */
  fitLine: [number, number][] | null;
}

export interface UseScatterFit {
  showFit: boolean;
  setShowFit: (value: boolean) => void;
  /** Fit window in x-axis units, clamped to the current data range. */
  window: { low: number; high: number };
  setWindow: (window: { low: number; high: number }) => void;
  manualOffsetOn: boolean;
  setManualOffsetOn: (value: boolean) => void;
  manualOffset: number;
  setManualOffset: (value: number) => void;
  zoomToWindow: boolean;
  setZoomToWindow: (value: boolean) => void;
  /**
   * The chart window when "Zoom to window" is on: the selected points (inside the
   * window whenever it applies, otherwise every point handed in), grown 10%.
   * Null means "draw the whole run".
   */
  zoom: PlotBox | null;
  /** Per-series split + fit, index-aligned with the `series` argument. */
  results: ScatterFitResult[];
}

export interface ScatterFitOptions {
  /** Offer the held-intercept option alongside the free fit. */
  allowManualOffset?: boolean;
  /** The window before the user has moved it; the full range when omitted. */
  defaultWindow?: { low: number; high: number };
  /** Lower end of the window's range (0 unless the axis runs negative). */
  minX?: number;
  /**
   * Apply the window (grey out points outside it) even with the fit hidden, for
   * plots that keep their window slider on screen at all times.
   */
  windowAlwaysOn?: boolean;
}

/**
 * The fit half of a scatter plot: the "show fit" toggle, the x-axis fit window,
 * the optional held-intercept offset, and the resulting per-series split and
 * least-squares fit.
 *
 * State is persisted per `storageKey`, so it survives both changing runs and the
 * unmount `CollapsibleSection` performs when its section is collapsed. The window
 * is stored raw and **clamped against `maxX` on read**: a window carried over from
 * a run with a wider range collapses to the new run's maximum rather than
 * stranding the plot, without overwriting the stored preference.
 */
export function useScatterFit(
  series: [number, number][][],
  maxX: number,
  storageKey: string,
  { allowManualOffset = false, defaultWindow, minX = 0, windowAlwaysOn = false }: ScatterFitOptions = {},
): UseScatterFit {
  const [stored, setStored] = useStoredState<StoredFit>(
    `runAuxSensor.fit.${storageKey}`,
    FIT_DEFAULTS,
    reviveFit,
  );

  const patch = (next: Partial<StoredFit>) => setStored({ ...stored, ...next });

  const { showFit, manualOffsetOn, manualOffset, zoomToWindow } = stored;
  const { low, high } = clampFitWindow(stored.window, maxX, defaultWindow, minX);
  const useWindow = windowAlwaysOn || showFit;
  const holdOffset = allowManualOffset && manualOffsetOn;

  const results = useMemo<ScatterFitResult[]>(
    () => series.map((points) => {
      const split = partitionByRange(points, useWindow ? low : 0, useWindow ? high : Infinity);
      // Only fit when the readout is actually shown — the fit is two passes over
      // every point, and off-state results are discarded.
      const fit = showFit
        ? (holdOffset ? linearFitFixedIntercept(split.included, manualOffset) : linearFit(split.included))
        : null;
      return { ...split, fit, fitLine: fitLinePoints(split.included, fit) };
    }),
    [series, showFit, useWindow, low, high, holdOffset, manualOffset],
  );

  const zoom = useMemo(
    () => (zoomToWindow ? zoomBox(results.map((r) => r.included)) : null),
    [results, zoomToWindow],
  );

  return {
    showFit,
    setShowFit: (value) => patch({ showFit: value }),
    window: { low, high },
    setWindow: (value) => patch({ window: value }),
    manualOffsetOn,
    setManualOffsetOn: (value) => patch({ manualOffsetOn: value }),
    manualOffset,
    setManualOffset: (value) => patch({ manualOffset: value }),
    zoomToWindow,
    setZoomToWindow: (value) => patch({ zoomToWindow: value }),
    zoom,
    results,
  };
}
